import { BaseEntity } from "./BaseModel";

export interface LoanType {
  loanTypeId: number;
  loanTypeName: string;
  loanTypeDescription: string;
}

export interface CreateLoanParams
  extends BaseEntity,
    BorrowerInformation,
    LoanInformation,
    GuarantorInformation,
    LoanEconomics {
  loanTypeId: number;
}

export interface CreateLoanParamsBridgeLoan
  extends BaseEntity,
    BridgeLoanBorrowerInformation,
    BridgeLoanGuarantorInformation,
    BridgeLoanLoanInformation,
    BridgeLoanLoanEconomics {
  loanTypeId: number;
  loanDetailId: any;
}

export enum LoanStage {
  pre = "PRE",
  post = "POST",
  fes = "FES",
  ps = "PS",
  tv = "TOORVAL",
}
export enum LoanStageToReadable {
  PRE = "Pre Close",
  POST = "Post Close",
  FES = "GuideLine Evaluation",
  TOORVAL = "ToorVal",
}
export interface CreateLoanParamsThirtyYearLoan
  extends BaseEntity,
    ThirtyYearBorrowerInformation,
    ThirtyYearGuarantorInformation,
    ThirtyYearLoanInformation,
    ThirtyYearLoanEconomics {
  loanTypeId: number;
}

export interface BridgeLoanBorrowerInformation {
  borrowerEmail: string;
  borrowerType: string;
  borrowingEntityName: string;
  firstName: string;
  lastName: string;
  foreignNationalString: string;
  loanUserMapId?: number;
  partyId?: string;
  originalCreditScoreMedian: string;
  originalCreditReportDate: Date | null;
  borrowerExperience: string;
  heavyRehabExperience?: string;
  borrowerGUCExperience?: string;
  billingAddress: string;
  billingPhoneNumber: string;
  isPrimaryBorrower: boolean;
  borrowerLoanUserSequence?: string;
  eiNumber?: string;
  ssNumber?: string;
  passportExpirationDate?: Date;
  city?: string;
  state?: string;
  pincode?: string;
  locationValidationStatus?: string;
}

export interface BridgeLoanGuarantorInformation {
  creditScore: string;
  guarantorEmail: string;
  guarantorExperience: string;
  guarantorLastName: string;
  loanUserMapId?: number;
  guarantorFirstName: string;
  partyId?: string;
  originalCreditReportDate: Date | null;
  pOEntity: string;
  foreignNationalString: string;
  isPrimaryGuarantor: boolean;
  loanUserSequence?: string;
  guarantorLoanUserSequence?: string;
  eiNumber?: string;
  ssNumber?: string;
  passportExpirationDate?: Date;
  city?: string;
  state?: string;
  pincode?: string;
  locationValidationStatus?: string;
}

export interface BridgeLoanLoanInformation {
  primaryLoanID: string;
  numberOfProperties: string;
  extensionOption: string;
  loanPurpose: string;
  selectedLoanStructure: string;
  selectedRecourse: string;
  selectedCross: string;
  selectedToorakProduct: string;
  cutOffDate: Date | null;
  originationDate: Date | null;
  firstPaymentDate: Date | null;
  maturityDate: Date | null;
  pledgeOfEquity: string;
  sellerPartyId: string;
}

export interface BridgeLoanLoanEconomics {
  interestRate: string;
  accrualType: string;
  financialInterestReserve: string;
  loanAmount: string;
  maxLoanAmount: string;
  cutOffLoanAmount: string;
  cutOffMaxLoanAmount: string;
  budgetAmount: string;
  cashOutAmount: string;
  discountPoints: string;
  insuranceAndAmortization: string;
  financedBudgetAmount: string;
}

export interface BridgeLoanLoanCharacterisations {
  customerType: string;
  purchaseOrRefinance: string;
  rehabType: string;
  stabilizedFlag: string;
  ficoType: string;
  cashOutFlag: string;
  loanCharacterisation: string;
  loanPurpose?: string;
  tier?: string;
}

export interface BridgeLoanLoanFeatures {
  originalAsIsLtv: string;
  originalAsRepairedLtv: string;
  originalLtc: string;
  originalTermToMaturity: string;
  blendedLtcAsCompleted?: string;
  arvLtvAsCompleted?: string;
  blendedLTCWithFundedIRAsCompleted?: string;
}

export interface PropertyLoanFeatures {
  toorakLoanTypeRehab: string;
  cbsa: string;
  medianValue: string;
  ltmHpa: string;
  daysOnMarket: string;
}

export interface BorrowerInformation {
  borrowerEmail: string;
  creditScoreActual: number;
  creditScoreBucket: string;
  foreignNationalString: string;
}
export interface GuarantorInformation {
  guarantorEmail: string;
  creditScoreActual: number;
  creditScoreBucket: string;
}

export interface ThirtyYearBorrowerInformation {
  borrowerType: string;
  borrowerEmail: string;
  borrowingEntityName: string;
  billingAddress: string;
  billingPhoneNumber: string;
  // [k: string]: string;
}

export interface ThirtyYearGuarantorInformation {
  guarantorLastName: string;
  guarantorFirstName: string;
  foreignNationalString: string;
  creditScore: string;
  originalCreditReportDate: Date | null;
  guarantorEmail: string;
  pOEntity: string;
  isPrimaryGuarantor: boolean;
}

export interface ThirtyYearLoanInformation {
  rateLockPeriod?: any;
  sizerRateLockPeriod?: any;
  primaryLoanID: string;
  loanPurpose: string;
  selectedToorakProduct: string;
  cutOffDate: Date | null;
  originationDate: Date | null;
  firstPaymentDate: Date | null;
  maturityDate: Date | null;
  selectedRecourse: string;
  selectedCross: string;
  numberOfProperties: string;
  condoEligibility?: string;
  calculatedCondoEligibility?: string;
  defaultRate?: any;
  sellerPartyId: string;
}

export interface ThirtyYearLoanEconomics {
  financialInterestReserve: string;
  loanAmount: string;
  currentLoanBalance: string;
  interestRate: string;
  rateType: string;
  accrualType: string;
  discountPoints: string;
  cashOutAmount: string;
  armProductType: string;
  armIndex: string;
  grossArmMargin: string;
  interestOnlyPeriod: string;
  originalMonthlyPiPayment: string;
  initialRateAdjustmentPeriod: string;
  initialPayAdjustmentPeriod: string;
  initialPeriodicCap: string;
  lifeRateCap: string;
  subsequentPeriodicCap: string;
  paymentAdjustmentFrequency: string;
  rateAdjustmentFrequency: string;
  prepaymentPenaltyMonths: string;
  prepayPenaltyType: string;
  costToLoanRatio: string;
  debtServiceCoverageRatio: string;
  borrowerLiquidity: string;
  originalQualifyingPaymentMonthly: string;
  qualifyingPaymentInReservesMonthly: string;
  lifetimeMinRate: string;
  subordinateFinancing: string;
  businessPurposeOccupancy: string;
  lifetimeMaxRate: string;
  // totalOriginationAndDiscountPoints?: string;
  // [k: string]: string;
}

export interface ThirtyYearLoanCharacterisations {
  loanType: string;
  ficoBucket: string;
  finalLoanCharacterization?: string;
}

export interface ThirtyYearLoanFeatures {
  originalLTV: string;
  originalLTC: string;
  originalTermToMaturity: string;
}

export interface ThirtyYearLoanPropertyLocation {
  address: LocationSuggestion | null;
  city: string;
  state: string;
  zipCode: string;
}

export interface ThirtyYearLoanPropertyInformation {
  propertyType: string;
  numberOfUnits: string;
  propertyValuationDate: Date | null;
  squareFootage: string;
  percentageOccupied: string;
}

export interface ThirtyYearLoanPropertyEconomics {
  originalAppraisalValue: string;
  purchasePrice: string;
  costBasis: string;
  annualPropertyTaxes: string;
  annualHazardInsurance: string;
  annualFloodInsurance: string;
  annualHoaFee: string;
  thirdPartyValuation: string;
}

export interface LoanInformation {
  appraisalVendor: string;
  loanAmount: number;
  propertyAcquire_date: string;
}

export interface LoanEconomics {
  closingCost: number;
  loanTerm: number;
  interestRate: number;
  prepaymentPenalty: number;
}

// eslint-disable-next-line @typescript-eslint/class-name-casing
export interface preAssignedURL {
  BucketName?: string;
  Bucketkey: string;
}
export interface PropertyInformation {
  propertyType: string;
  numberOfUnits: string;
  propertyValuationDate: Date | null;
  propertyAcquisitionDate: Date | null;
  isPropertyPurchasedLast2Years?: string;
  changeInUseCase: string;
  preRehabSquareFootage: string;
  postRehabSquareFootage: string;
  percentageOccupied: string;
  squareFootage: string;
  toorvalId: string;
}

export interface LocationSuggestion {
  street_line: string;
  secondary: string;
  city: string;
  state: string;
  zipcode: string;
  entries: number;
}

export interface PropertyLocation {
  address: LocationSuggestion | null;
  city: string;
  state: string;
  zipCode: string;
  duplicateLoanIds: string[];
  duplicatePropertyInfo: any;
  invalidAddress: boolean;
  locationValidationStatus?: string;
}

export interface PropertyEconomics {
  originalAppraisalValue: string;
  originalRepairedAppraisalValue: string;
  purchasePrice: string;
  costBasis: string;
  grossPotentialRent: string;
  assignmentFees?: string;
  insurance: string;
  rentalCashflowRatio: string;
  annualPropertyTaxes: string;
  annualHazardInsurance: string;
  annualFloodInsurance: string;
  annualHoaFee: string;
  thirdPartyValuation: string;
  appraisalReported: string;
  decliningMarkets: string;
}

export enum LoanTypes {
  BridgeLoan = "Bridge Loan",
  ThirtyYearLoan = "30 Year Loan",
  TwoOneLoan = "2 + 1 Loan",
}

enum LoanStatus {
  InProgress = "In Progress",
  Submitted = "Submitted",
  Approved = "Approved",
  Rejected = "Rejected",
}

enum LoanSource {
  Manual = "Manual",
  ExcelUpload = "Excel Upload",
  FrontEndSizer = "Front End Sizer",
}

export interface LoanList {
  originalLoanId: number;
  loanType: LoanTypes;
  loanPurpose: string;
  propertyAddress: string;
  user: string;
  workflowState?: string;
  assignedTo?: string;
  loanSubstate?: string;
  loanProcessType?: string;
  roleOfAssignee?: string;
  toorakLoanId: string;
  loanAmount: number;
  interestRate: number;
  numberOfProperties: number;
  source: LoanSource;
  loanCreateDate: string;
  loanSubmissionDate: string;
  status: LoanStatus;
  /*
   type coming from BE and not story
  */
  loan_status: string;
  loan_status_detail_id: string;
  loan_economics_id: {
    loan_economics_id: string;
    closing_cost: number;
    loan_term: number;
    interest_rate: number;
  };
  borrower_info_id: {
    borrower_email: string;
    credit_score: number;
  };
  loan_info: {
    loan_info_id: string;
    loan_amount: number;
    property_acquire_date: string;
  };
}

export interface LoanEvaluationRequestProperty {
  propertyId: number;
  propertyEcon: {
    grossPotentialRent: number;
    originalAsIsAppraisalValue: number;
    purchasePrice: number;
    annualPropertyTaxes: string;
    insurance: number;
    originalAsRepairedAppraisedValue: number;
    costBasisAfterCostIncurred: number;
    rentalCashflowRatio: number;
  };
  propertyInfo: {
    propertyType: string;
    numberOfUnits: number;
    mostRecentPropertyValuationDate: string;
    propertyAcquisitionDate: string;
    changeInUseCase: "Y" | "N";
    preRehabSquareFootage: number;
    postRehabSquareFootage: number;
    appraisalReportedNotOrNcf: number;
    percentOccupied: number;
  };
  propertyLocation: {
    postalCode: string;
  };
}

export interface LoanEvaluationRequest {
  loanFact: {
    loan: {
      loanDetail?: {
        originatorPartyId: string;
      };
      loanEcon: {
        cutOffdateLoanAmount: number;
        cutOffDateMaximumLoanAmount: number;
        totalBudgetAmount: number;
        financedInterestReserve: number;
        originalLoanAmount: number;
        originalMaximumLoanAmount: number;
        cashOutAmount: number;
        accrualType: string;
        interestRate: number;
        totalOriginationAndDiscountPoints: number;
        insuranceAndAmortization: number;
        financedBudgetAmount: number;
      };
      loanInfo: {
        loanId: string;
        loanType: string;
        product: string;
        loanStage: string;
        loanPurpose: string;
        loanStructure: string;
        toorakProduct: string;
        recourse: "Y" | "N";
        originationDate: string | null;
        firstPaymentDateOfLoan: string | null;
        originalMaturityDate: string | null;
        pledgeOfEquity: "Y" | "N";
      };
      loanUserMap?: any;
      loanRuleVersions?: any[];
    };
    properties: LoanEvaluationRequestProperty[];
  };
  ruleAttributs: {
    ruleGroup: string[];
    ruleField: [];
    ruleResult: string[] | [];
  };
  resultObject: {};
}
export interface ThirtyYearLoanEvaluationRequestProperty {
  propertyId: number;
  propertyEcon: {
    originalAsIsAppraisalValue: number;
    purchasePrice: number;
    annualPropertyTaxes: number;
    thirdPartyValuation: number;
    costBasisAfterCostIncurred: number;
    annualHazardInsurance: number;
    annualFloodInsurance: number;
    annualHOAFee: number;
  };
  propertyInfo: {
    propertyType: string;
    numberOfUnits: number;
    mostRecentPropertyValuationDate: string;
    propertyAcquisitionDate: string;
    percentOccupied: number;
  };
  propertyLocation: {
    postalCode: string;
    city: string;
    state: string;
  };
  units: {
    rentDescription: string;
    annualMarketRent: number | null;
    annualInPlaceLeaseRent: number | null;
    twelveMonthsOperatingRent: number | null;
    annualBorrowerRentEstimate: number | null;
  };
}

export interface ThirtyYearLoanEvaluationRequest {
  loanFact: {
    loan: {
      loanEcon: {
        rateType: string;
        subOrdinateFinancing: string;
        prePaymentPenaltyMonths: number;
        businessPurposeOccupancy: string;
        interestOnlyPeriod: number;
        financedInterestReserve: number;
        originalLoanAmount: number;
        cashOutAmount: number;
        accrualType: string;
        dscr: number;
        interestRate: number;
        totalOriginationAndDiscountPoints: number;
      };
      loanInfo: {
        loanId: string;
        loanType: string;
        product: string;
        loanStage: string;
        loanPurpose: string;
        toorakProduct: string;
        recourse: string;
        condoEligibility: string;
        originationDate: string | null;
        firstPaymentDateOfLoan: string | null;
        crossCollaterlization: string;
        originalMaturityDate: string | null;
      };
      loanUserMap?: any;
    };
    properties: ThirtyYearLoanEvaluationRequestProperty[];
  };
  ruleAttributs: {
    ruleGroup: string[];
    ruleField: [];
    ruleResult: string[] | [];
  };
  resultObject: {};
}

export enum FundingTypes {
  TableFunding = "table_funding",
  Normal = "normal",
}
